import { useEffect, useRef } from 'react';
import { IS_TEST_ENV, TURNSTILE_SITE_KEY } from '../../config';
import type { TurnstileAction } from '../../types/domain';
import { c } from '../../styles/theme';

interface TurnstileApi {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      theme: 'auto';
      size: 'flexible';
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-lyrixsa-turnstile]');
    const script = existing ?? document.createElement('script');
    const onLoad = () => {
      if (window.turnstile) resolve();
      else onError();
    };
    const onError = () => {
      script.remove();
      scriptPromise = null;
      reject(new Error('TURNSTILE_SCRIPT_FAILED'));
    };
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });
    if (!existing) {
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.lyrixsaTurnstile = 'true';
      document.head.appendChild(script);
    }
  });
  return scriptPromise;
}

export function TurnstileWidget({
  action,
  resetKey,
  onTokenChange,
}: {
  action: TurnstileAction;
  resetKey: number;
  onTokenChange: (token: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const onTokenChangeRef = useRef(onTokenChange);
  onTokenChangeRef.current = onTokenChange;

  useEffect(() => {
    if (IS_TEST_ENV) {
      onTokenChangeRef.current(`test-turnstile-${action}`);
      return () => onTokenChangeRef.current('');
    }
    if (!TURNSTILE_SITE_KEY || !container.current) {
      onTokenChangeRef.current('');
      return;
    }
    let active = true;
    void loadTurnstile()
      .then(() => {
        if (!active || !container.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(container.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          theme: 'auto',
          size: 'flexible',
          callback: (token) => onTokenChangeRef.current(token),
          'expired-callback': () => onTokenChangeRef.current(''),
          'error-callback': () => onTokenChangeRef.current(''),
        });
      })
      .catch(() => onTokenChangeRef.current(''));
    return () => {
      active = false;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
      onTokenChangeRef.current('');
    };
  }, [action]);

  useEffect(() => {
    onTokenChangeRef.current('');
    if (IS_TEST_ENV) {
      onTokenChangeRef.current(`test-turnstile-${action}`);
    } else if (widgetId.current && window.turnstile) {
      window.turnstile.reset(widgetId.current);
    }
  }, [action, resetKey]);

  if (!IS_TEST_ENV && !TURNSTILE_SITE_KEY) {
    return (
      <p role="alert" style={{ fontSize: 12, color: c.danger, margin: '8px 0' }}>
        Human verification is temporarily unavailable.
      </p>
    );
  }

  return (
    <div
      ref={container}
      data-turnstile-action={action}
      style={{ width: '100%', margin: '10px 0' }}
    />
  );
}
